// v0.20.14 — branded QR rendering + scanned-link parsing.
//
// Branded QR: standard black-on-white modules, but the three finder-pattern
// "anchors" keep their black frame/centre while the light ring INSIDE each is
// filled cyan (a brand touch; the rest stays fully scannable black-on-white).

import qrcode from "qrcode-generator";
import { canonicalizeCode, isWalletCode } from "./codes";
import { parseAmountSegment } from "./money";

const CYAN = "#7df9ff";
const DARK = "#000000";

export function brandedQrSvg(data: string, cell = 6, margin = 2): string {
  const qr = qrcode(0, "M");
  qr.addData(data);
  qr.make();
  const n = qr.getModuleCount();
  const size = (n + margin * 2) * cell;
  const inFinder = (r: number, c: number) =>
    (r < 7 && c < 7) || (r < 7 && c >= n - 7) || (r >= n - 7 && c < 7);

  let rects = "";
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      const dark = qr.isDark(r, c);
      let fill: string | null = null;
      if (dark) fill = DARK; // all dark modules stay black
      else if (inFinder(r, c)) fill = CYAN; // light ring inside an anchor → cyan
      if (!fill) continue;
      const x = (c + margin) * cell;
      const y = (r + margin) * cell;
      rects += `<rect x="${x}" y="${y}" width="${cell}" height="${cell}" fill="${fill}"/>`;
    }
  }
  return `<svg viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg"><rect width="${size}" height="${size}" fill="#fff"/>${rects}</svg>`;
}

// --- scanned-link parsing ---------------------------------------------------

export interface ScannedLink {
  slug: string; // canonical wallet code
  amount?: number;
  reference?: string;
  /** The link came from M® Checkout's QR on a computer (`?qr=1`): the pay page's success card points back there. */
  scanned?: boolean;
  /** 1.5.21 — the merchant site that drew the QR (`?site=`); the pay page asks the Worker about it. */
  site?: string;
}

/**
 * Parse a scanned string into an M pay target, or null if it isn't one.
 * Accepts M's links with or without a scheme — on mdrop.it (the short link M
 * prints) or globalmoneyapp.com (the app's own origin, which M® Checkout's QR
 * carries since 1.5.17; app 1.5.19); the code segment is case/hyphen-agnostic
 * and returned canonical:
 *   mdrop.it/{CODE}
 *   mdrop.it/{CODE}/USD{amount}
 *   mdrop.it/{CODE}/USD{amount}/{reference}
 */
export function parseMLink(text: string): ScannedLink | null {
  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`);
  } catch {
    return null;
  }
  if (!/(^|\.)(mdrop\.it|globalmoneyapp\.com)$/i.test(url.hostname)) return null;

  const segs = url.pathname.split("/").map((s) => s.trim()).filter(Boolean);
  const slug = canonicalizeCode(segs[0] ?? "");
  if (!isWalletCode(slug)) return null;

  const out: ScannedLink = { slug };
  if (url.searchParams.get("qr") === "1") out.scanned = true; // M® Checkout's QR on a computer (1.5.17)
  const site = url.searchParams.get("site");
  if (out.scanned && site && /^https?:\/\//i.test(site)) out.site = site.slice(0, 300); // 1.5.21
  if (segs[1]) {
    const amt = parseAmountSegment(segs[1]);
    if (amt != null) {
      out.amount = amt;
      if (segs[2]) {
        try {
          out.reference = decodeURIComponent(segs[2]);
        } catch {
          out.reference = segs[2];
        }
      }
    }
  }
  return out;
}
